import { useState } from 'react';
import { FieldValues, FormState, UseFormRegister, UseFormSetValue } from 'react-hook-form';

import { FieldLayout, Fieldset, FormField, Input, Label } from '@anwb/poncho/components/forms';
import Panel from '@anwb/poncho/components/panel';
import { request } from '@anwb/poncho/utilities/api';
import { useTransformVisualValue } from '@anwb/poncho/utilities/form-helpers';
import { formatCity, formatPostalCode } from '@anwb/poncho/utilities/formatters';
import cityRegexValidation from '@anwb/poncho/validations/validations-city';
import houseNumberRegexValidation from '@anwb/poncho/validations/validations-house-number';
import houseNumberSuffixRegexValidation from '@anwb/poncho/validations/validations-house-number-suffix';
import postcodeRegexValidation from '@anwb/poncho/validations/validations-postal-code';
import streetRegexValidation from '@anwb/poncho/validations/validations-street';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import { PLCustomerData } from 'types';
import { AddressResponse } from 'types/addressResponse';
import addressConfig from 'utils/getAddressAPIKeys';

import { handleFormGaEvent } from '../utils';

import { StyledTypography } from './styles/fieldset-caption.styled';

type AddressProps = {
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	getValues: () => FieldValues;
	formState: FormState<PLCustomerData>;
	trigger: (name?: string | string[]) => Promise<boolean>;
};

export const Address = ({ register, setValue, getValues, formState, trigger }: AddressProps) => {
	const [loadingAddress, setLoadingAddress] = useState(false);

	const [, transformedPostalCode, handlePostalCodeChange] = useTransformVisualValue('', [
		formatPostalCode,
	]);
	const [, transformedCity, handleCityChange] = useTransformVisualValue('', [formatCity]);
	const gaFunnelInfo = useGaFunnelInfo();

	const getAddress = async () => {
		const { postalCode, houseNumber, houseNumberSuffix } = getValues();

		if (postalCode && houseNumber) {
			setLoadingAddress(true);

			await request(
				{
					url: `/v1/addresses/${postalCode}/${houseNumber}/${houseNumberSuffix}`,
				},
				{
					clientId: addressConfig.client_id,
				}
			)
				.then((response) => {
					if (!response.ok) {
						throw Error(response.statusText);
					}

					return response;
				})
				.then((response) => response.json())
				.then((data: AddressResponse) => {
					setValue('street', data.Straat, { shouldDirty: true });
					// @ts-ignore construct a custom event, because useTransformVisualValue doesn't accept raw value
					handleCityChange({ target: { value: data.Plaats } });
					setValue('city', formatCity(data.Plaats), { shouldDirty: true });
				})
				.catch((e) => {
					console.error('Error in getting address', e);
				})
				.finally(() => {
					setLoadingAddress(false);
				});
		}
	};

	return (
		<Panel hasOuterSpacing={false}>
			<Panel.Content>
				<Fieldset
					legend={
						<StyledTypography variant="component-title" tagName="h2">
							Adresgegevens
						</StyledTypography>
					}
				>
					<FieldLayout>
						<FormField size="s" label={<Label text="Postcode" reference="postalCode" required />}>
							<Input
								reference="postalCode"
								placeholder="Bijv.: 1234AB"
								{...register('postalCode', {
									required: 'Je hebt je postcode nog niet ingevuld.',
									pattern: {
										value: new RegExp(postcodeRegexValidation),
										message: 'Je postcode bestaat uit 4 cijfers en 2 letters. Bijv.: 1234AB',
									},
								})}
								onChange={(event) => {
									handlePostalCodeChange(event);
									setValue('postalCode', event.target.value);
								}}
								onBlur={async (event) => {
									if (transformedPostalCode) {
										setValue('postalCode', transformedPostalCode, { shouldDirty: true });
										await trigger('postalCode');
									}
									getAddress();
									handleFormGaEvent('postalCode', formState, event, gaFunnelInfo);
								}}
							/>

							{formState.errors.postalCode && (
								<FormField.Error aria-live="assertive">
									{formState.errors.postalCode.message}
								</FormField.Error>
							)}
						</FormField>
						<FormField
							size="s"
							label={<Label text="Huisnummer" reference="houseNumber" required />}
						>
							<Input
								reference="houseNumber"
								placeholder="Bijv.: 1"
								{...register('houseNumber', {
									required: 'Je hebt je huisnummer nog niet ingevuld.',
									pattern: {
										value: new RegExp(houseNumberRegexValidation),
										message: 'Je hebt je huisnummer onjuist ingevuld.',
									},
								})}
								onChange={(event) => {
									setValue('houseNumber', event.target.value, { shouldDirty: true });
								}}
								onBlur={async (event) => {
									await trigger('houseNumber');
									getAddress();
									handleFormGaEvent('houseNumber', formState, event, gaFunnelInfo);
								}}
							/>

							{formState.errors.houseNumber && (
								<FormField.Error aria-live="assertive">
									{formState.errors.houseNumber.message}
								</FormField.Error>
							)}
						</FormField>
						<FormField size="m" label={<Label text="Toevoeging" reference="houseNumberSuffix" />}>
							<Input
								reference="houseNumberSuffix"
								placeholder="Bijv.: A"
								{...register('houseNumberSuffix', {
									pattern: {
										value: new RegExp(houseNumberSuffixRegexValidation),
										message: 'Je hebt de toevoeging van je huisnummer onjuist ingevuld.',
									},
								})}
								onChange={(event) => {
									setValue('houseNumberSuffix', event.target.value);
								}}
								onBlur={async (event) => {
									const { houseNumberSuffix } = getValues();
									setValue('houseNumberSuffix', houseNumberSuffix.trim(), { shouldDirty: true });
									await trigger('houseNumberSuffix');

									getAddress();
									handleFormGaEvent('houseNumberSuffix', formState, event, gaFunnelInfo);
								}}
							/>

							{formState.errors.houseNumberSuffix && (
								<FormField.Error aria-live="assertive">
									{formState.errors.houseNumberSuffix.message}
								</FormField.Error>
							)}
						</FormField>
					</FieldLayout>
					<FieldLayout>
						<FormField size="m" label={<Label text="Straatnaam" reference="street" required />}>
							<Input
								reference="street"
								{...register('street', {
									required: 'Je hebt je straatnaam nog niet ingevuld.',
									pattern: {
										value: new RegExp(streetRegexValidation),
										message: 'Je hebt je straatnaam onjuist ingevuld.',
									},
								})}
								onChange={(event) => {
									setValue('street', event.target.value, { shouldDirty: true });
								}}
								onBlur={async (event) => {
									await trigger('street');
									handleFormGaEvent('street', formState, event, gaFunnelInfo);
								}}
								loading={loadingAddress}
							/>

							{formState.errors.street && (
								<FormField.Error aria-live="assertive">
									{formState.errors.street.message}
								</FormField.Error>
							)}
						</FormField>
					</FieldLayout>
					<FieldLayout>
						<FormField size="m" label={<Label text="Woonplaats" reference="city" required />}>
							<Input
								reference="city"
								{...register('city', {
									required: 'Je hebt je woonplaats nog niet ingevuld.',
									pattern: {
										value: new RegExp(cityRegexValidation),
										message: 'Je hebt je woonplaats onjuist ingevuld.',
									},
								})}
								loading={loadingAddress}
								onChange={(event) => {
									handleCityChange(event);
									setValue('city', event.target.value);
								}}
								onBlur={async (event) => {
									if (transformedCity) {
										setValue('city', transformedCity, { shouldDirty: true });
										await trigger('city');
									}
									handleFormGaEvent('city', formState, event, gaFunnelInfo);
								}}
							/>

							{formState.errors.city && (
								<FormField.Error aria-live="assertive">
									{formState.errors.city.message}
								</FormField.Error>
							)}
						</FormField>
					</FieldLayout>
				</Fieldset>
			</Panel.Content>
		</Panel>
	);
};
